import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { OrderDetailResponse } from '@shared/api-responses';
import { apiFetch } from '../hooks/useApi';
import PickList, { type PickListItem } from '../components/PickList';

export default function OrderPrint() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<OrderDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiFetch<OrderDetailResponse>(`/orders/${id}`)
      .then((d) => setData(d))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load order'))
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-trigger print dialog once content has rendered
  useEffect(() => {
    if (data && !error) {
      const timer = setTimeout(() => window.print(), 350);
      return () => clearTimeout(timer);
    }
  }, [data, error]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white text-black">
        <p className="text-sm text-gray-500">Loading pick list...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white text-black">
        <div className="text-center">
          <p className="text-sm font-medium text-red-600">{error ?? 'Order not found'}</p>
          <button
            type="button"
            onClick={() => window.close()}
            className="mt-3 rounded-md border border-gray-300 px-3 py-1.5 text-xs"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const items: PickListItem[] = data.items.map((i) => ({
    name: i.name,
    category: i.category,
    target: i.target,
    actual: i.actual,
    needed: i.needed,
  }));

  return (
    <div className="min-h-dvh bg-gray-100 print:bg-white">
      {/* Print toolbar — hidden on paper */}
      <div className="no-print sticky top-0 z-10 border-b border-gray-300 bg-white shadow-sm">
        <div className="mx-auto max-w-3xl px-4 py-2 flex items-center justify-between">
          <p className="text-xs text-gray-600">Pick list ready · use your browser's Print dialog (Ctrl/Cmd-P)</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-md bg-black text-white px-3 py-1.5 text-xs font-medium hover:bg-gray-800"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Sheet */}
      <div className="px-4 py-6 print:p-0">
        <div className="bg-white rounded shadow-sm print:shadow-none print:rounded-none p-6 print:p-0">
          <PickList
            stationName={data.station.name}
            stationCode={data.station.code}
            orderId={data.order.id}
            items={items}
            submittedAt={data.order.created_at}
          />
        </div>
      </div>
    </div>
  );
}
